// src/components/templates/AddInteractionModal.js

import React, { useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "styled-components";
import AddInteractionForm from "../organisms/AddInteractionForm";
import { storage, db } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc } from "firebase/firestore";
import PropTypes from "prop-types";

const AddInteractionModal = ({
  open,
  handleClose,
  videoId,
  onAddInteraction,
  initialTimestamp, // New prop
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const handleFormSubmit = async (interactionData) => {
    setLoading(true);
    try {
      // Upload media to Firebase Storage
      const mediaRef = ref(
        storage,
        `videos/${videoId}/interactions/${Date.now()}_${
          interactionData.mediaFile.name
        }`
      );
      const snapshot = await uploadBytes(mediaRef, interactionData.mediaFile);
      const mediaURL = await getDownloadURL(snapshot.ref);

      // Prepare interaction data
      const newInteraction = {
        title: interactionData.title,
        description: interactionData.description,
        callToAction: interactionData.callToAction,
        callToActionURL: interactionData.callToActionURL,
        mediaType: interactionData.mediaType,
        mediaURL: mediaURL,
        timestamp: interactionData.timestamp,
        createdAt: new Date(),
      };

      // Save interaction to Firestore
      const interactionsCollection = collection(
        db,
        "videos",
        videoId,
        "interactions"
      );
      const docRef = await addDoc(interactionsCollection, newInteraction);
      newInteraction.id = docRef.id;

      // Update parent component
      onAddInteraction(newInteraction);

      // Close modal
      handleClose();
    } catch (error) {
      console.error("Error adding interaction:", error);
      alert("Failed to add interaction. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiPaper-root": {
          background: "transparent",
          borderRadius: "25px",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.colors.headerPurple,
          color: "white",
          fontFamily: "basic-sans",
          fontWeight: "700",
        }}
      >
        Add New Interaction
      </DialogTitle>
      <DialogContent
        sx={{ backgroundColor: theme.colors.mediumPurple, color: "white" }}
      >
        <AddInteractionForm
          initialTimestamp={initialTimestamp || 0} // Use initialTimestamp
          onSubmit={handleFormSubmit}
          onCancel={handleClose}
        />
      </DialogContent>
      {loading && (
        <DialogActions
          sx={{ backgroundColor: theme.colors.mediumPurple, color: "white" }}
        >
          <CircularProgress />
        </DialogActions>
      )}
    </Dialog>
  );
};

AddInteractionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  videoId: PropTypes.string.isRequired,
  onAddInteraction: PropTypes.func.isRequired,
  initialTimestamp: PropTypes.number, // Optional prop
};

export default AddInteractionModal;
